import type { DbmImageResult } from '@ps/web-shared-api-types/v1-preview/SwPkrSpinAndGo';
import produce from 'immer';
import type { DeepRequired } from 'utility-types';

import type { Game } from '../types/Game';
import type { WatchGamesUpdatePayload } from '../types/WatchGames';
import { WatchGamesTabs } from '../types/WatchGames';

import { defaultState } from './defaultState';
import type { PokerSpinAndGoState } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): PokerSpinAndGoState => produce(defaultState, (draft) => draft),
  gamesRequested: (state: State): State => ({
    ...state,
    isLoading: true
  }),
  gamesLoadSuccess: (state: State, payload: Game[]): State => ({
    ...state,
    games: payload,
    isLoading: false
  }),
  gamesLoadFailure: (state: State): State => ({
    ...state,
    games: [],
    isLoading: false,
    hasError: true
  }),
  setRandomRBWTicker: (state: State): State => {
    const {
      tickerMessage,
      watchGames: { recentBigWins }
    } = state;
    const recentBigWinsGame = recentBigWins?.[Math.floor(Math.random() * recentBigWins?.length)];

    if (!tickerMessage && recentBigWinsGame?.prizePool) {
      return {
        ...state,
        tickerMessage: {
          prize: recentBigWinsGame.winAmount,
          currency: recentBigWinsGame.currency,
          scalePM: recentBigWinsGame.scalePM,
          buyIn: recentBigWinsGame.buyIn,
          winner: recentBigWinsGame.winner,
          tabType: WatchGamesTabs.recentBigWins
        }
      };
    }

    return state;
  },
  clearWatchGamesTicker: (state: State): State => ({
    ...state,
    tickerMessage: null
  }),
  updateWatchGames: (state: State, { watchGames: watchGamesResponse }: WatchGamesUpdatePayload) =>
    produce(state, (draft) => {
      const { hallOfFame, recentBigWins, bestLiveNow } = watchGamesResponse;
      const hallOfFameGames = hallOfFame?.hallOfFameList?.slice(0, 5) || [];
      const recentBigWinsGames = recentBigWins?.recentBigWinsList?.slice(0, 20) || [];
      const bestLiveNowGames = bestLiveNow?.bestLiveNowByPrize?.slice(0, 6) || [];
      const { tickerMessage, watchGames } = draft;
      const { userImageMap } = watchGames;

      if (!tickerMessage) {
        const newBestLiveNowGame = bestLiveNowGames.find(
          (newGame) =>
            !watchGames.bestLiveNow.some((prevGame) => prevGame.tournId === newGame.tournId)
        );

        if (newBestLiveNowGame) {
          draft.tickerMessage = {
            prize: newBestLiveNowGame.prizePool,
            currency: newBestLiveNowGame.currency,
            scalePM: newBestLiveNowGame.scalePM,
            tabType: WatchGamesTabs.liveNow
          };
        }
      }

      hallOfFameGames.forEach((item) => {
        if (!userImageMap[item.imageId] && userImageMap[item.imageId] !== '') {
          userImageMap[item.imageId] = null;
        }
      });

      recentBigWinsGames.forEach((item) => {
        if (!userImageMap[item.imageId] && userImageMap[item.imageId] !== '') {
          userImageMap[item.imageId] = null;
        }
      });

      draft.watchGames.hallOfFame = hallOfFameGames;
      draft.watchGames.recentBigWins = recentBigWinsGames;
      draft.watchGames.bestLiveNow = bestLiveNowGames;
    }),
  updateAvatarImages: (state: State, response: DeepRequired<DbmImageResult>[]) => {
    const { userImageMap } = state.watchGames;
    const updatedImagesMap = response.forEach(
      (item) => (userImageMap[item.imageId] = item.base64Image)
    );
    return {
      ...state,
      userImageMap: updatedImagesMap
    };
  }
};
